import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Input, Space, Tag, Button, Select } from 'antd';
import { SearchOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useState } from 'react';

interface BusBooking {
  id: number;
  bookingCode: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string | null;
  seatNumbers: number[];
  passengers: string;
  totalPrice: number;
  isPaid: boolean;
  status: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  busTrip: {
    id: number;
    departureCity: { nameUz: string; nameRu: string; nameEn: string };
    arrivalCity: { nameUz: string; nameRu: string; nameEn: string };
    departureBusStation: string;
    arrivalBusStation: string;
    departureTime: string;
    arrivalTime: string;
    pricePerSeat: number;
    busType: string;
    busNumber: string;
    busCompany: { id: number; name: string };
  };
}

export const BusBookingList = () => {
  const { show, create } = useNavigation();
  const [searchText, setSearchText] = useState('');

  const { tableProps, setFilters } = useTable<BusBooking>({
    resource: 'admin/bus/bookings',
    syncWithLocation: true,
  });

  const handleSearch = () => {
    const filters: any[] = [];
    if (searchText) {
      filters.push({ field: 'search', operator: 'contains', value: searchText });
    }
    setFilters(filters);
  };

  const handleStatusFilter = (status: string | undefined) => {
    const filters: any[] = [];
    if (status) {
      filters.push({ field: 'status', operator: 'eq', value: status });
    }
    if (searchText) {
      filters.push({ field: 'search', operator: 'contains', value: searchText });
    }
    setFilters(filters);
  };

  const handlePaymentFilter = (isPaid: string | undefined) => {
    const filters: any[] = [];
    if (isPaid !== undefined) {
      filters.push({ field: 'isPaid', operator: 'eq', value: isPaid });
    }
    if (searchText) {
      filters.push({ field: 'search', operator: 'contains', value: searchText });
    }
    setFilters(filters);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
  };

  const statusColors: Record<string, string> = {
    PENDING: 'orange',
    CONFIRMED: 'blue',
    CANCELLED: 'red',
    COMPLETED: 'green',
  };

  const statusLabels: Record<string, string> = {
    PENDING: 'Kutilmoqda',
    CONFIRMED: 'Tasdiqlangan',
    CANCELLED: 'Bekor qilingan',
    COMPLETED: 'Yakunlangan',
  };

  const columns = [
    {
      title: 'Kod',
      dataIndex: 'bookingCode',
      key: 'bookingCode',
      render: (code: string) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: 'Mijoz',
      key: 'client',
      render: (_: unknown, record: BusBooking) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.clientName}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.clientPhone}</div>
        </div>
      ),
    },
    {
      title: "Yo'nalish",
      key: 'route',
      render: (_: unknown, record: BusBooking) => (
        <div>
          <div>
            {record.busTrip?.departureCity?.nameUz} → {record.busTrip?.arrivalCity?.nameUz}
          </div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>
            {record.busTrip?.busCompany?.name}
          </div>
        </div>
      ),
    },
    {
      title: "Jo'nash",
      key: 'departure',
      render: (_: unknown, record: BusBooking) =>
        record.busTrip?.departureTime
          ? new Date(record.busTrip.departureTime).toLocaleString('uz-UZ', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '-',
    },
    {
      title: "O'rindiqlar",
      dataIndex: 'seatNumbers',
      key: 'seatNumbers',
      render: (seats: number[]) => seats?.join(', ') || '-',
    },
    {
      title: 'Summa',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (price: number) => formatCurrency(price),
    },
    {
      title: "To'lov",
      dataIndex: 'isPaid',
      key: 'isPaid',
      render: (isPaid: boolean) => (
        <Tag color={isPaid ? 'green' : 'orange'}>
          {isPaid ? "To'langan" : "To'lanmagan"}
        </Tag>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: 'Sana',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: BusBooking) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('admin/bus/bookings', record.id)}
        />
      ),
    },
  ];

  return (
    <List
      title="Avtobus buyurtmalari"
      headerButtons={
        <Space>
          <Input
            placeholder="Kod, telefon..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 180 }}
            onPressEnter={handleSearch}
          />
          <Select
            placeholder="To'lov"
            allowClear
            style={{ width: 130 }}
            onChange={handlePaymentFilter}
            options={[
              { value: 'true', label: "To'langan" },
              { value: 'false', label: "To'lanmagan" },
            ]}
          />
          <Select
            placeholder="Holat"
            allowClear
            style={{ width: 140 }}
            onChange={handleStatusFilter}
            options={[
              { value: 'PENDING', label: 'Kutilmoqda' },
              { value: 'CONFIRMED', label: 'Tasdiqlangan' },
              { value: 'CANCELLED', label: 'Bekor qilingan' },
              { value: 'COMPLETED', label: 'Yakunlangan' },
            ]}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => create('admin/bus/bookings')}
          >
            Yaratish
          </Button>
        </Space>
      }
    >
      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
